/**
 * PropertyResolver — Pure Canopy Resolution Engine
 *
 * Implements the Canopy spec's 5-step value resolution, state stack management,
 * inheritance cascade, and subscription notifications — with zero engine
 * dependencies. This is the algorithmic core of the Properties behavior.
 *
 * The resolver communicates with its host environment through two interfaces:
 * - `CanopyNodeAdapter` — tree traversal (ancestor walk, descendant cascade)
 * - `CanopyTransitionHost` — animation delegation (time/property-driven transitions)
 *
 * The host (Properties behavior) owns Babylon.js lifecycle, transform overrides,
 * system value polling, and external sheet loading. It delegates all resolution
 * and state logic to this class.
 *
 * See docs/canopy/ for the formal Canopy specification.
 */

import type {
    PropertyValue,
    PropertyDeclaration,
    PropertiesData,
    StateDefinition,
    TransformOverride,
    TransitionConfig,
    TimeTransition,
    PropertyTransition,
    CanopySubscriber,
    CanopyProvider,
    CanopyNodeAdapter,
    CanopyNodeChild,
    CanopyTransitionHost,
} from './types';
import type { Units } from '../../scenedata/behaviors/propertiesdata';
import { isTimeTransition, isPropertyTransition } from './types';
import { interpolateValue, computeDriverProgress } from './TransitionUtils';
import { toCanonical, fromCanonical, declarationUnits } from './units';

// ── State stack priority ──

/** Priority tiers for state stacking. Higher number = higher priority. */
const PSEUDO_STATE_PRIORITY: Record<string, number> = {
    ':active': 40,
    ':selected': 30,
    ':hover': 20,
};
const CUSTOM_STATE_PRIORITY = 10;

/** A state entry in the active stack. */
interface ActiveStateEntry {
    name: string;
    priority: number;
}

/**
 * An external state contribution pushed by a non-Properties source
 * (typically the Keyframes behavior). Contributions participate in the state
 * stack at custom-state priority; later contributions win ties.
 */
interface ContributionEntry {
    contributorId: number;
    name: string;
    def: StateDefinition;
    seq: number;
}

/** Tracks a single property-driven transition target. */
interface PropertyDriverTarget {
    config: PropertyTransition;
    fromValue: PropertyValue;
    toValue: PropertyValue;
}

/**
 * How many {@link readAtBase} calls are on the stack. Module-level, not
 * per-resolver: inheritance crosses resolvers, and an ancestor asked mid-walk
 * has to answer from the same view as the node that asked.
 */
let baseReadDepth = 0;

/**
 * Run `read` with every resolver answering as the file starts: declared values
 * and inheritance only — no custom or pseudo state, no external contribution,
 * no runtime override, no in-flight transition. Those are what a session is
 * showing, and none of them is written to the file (the state stack is not
 * serialized, and Pages does not apply page assignments on load — PAGES_SPEC
 * P-23). A read, not a switch: nothing is mutated and no subscriber hears it.
 * `read` must be synchronous.
 */
export function readAtBase<T>(read: () => T): T {
    baseReadDepth++;
    try {
        return read();
    } finally {
        baseReadDepth--;
    }
}

/**
 * Callback interface for events the host needs to handle.
 * These are things that require engine-specific behavior (transforms,
 * system values) that the pure resolver can't do itself.
 */
export interface ResolverCallbacks {
    /** Resolve a driver expression (e.g. "camera.distance") to a number */
    resolveDriverExpression(driverName: string): number | undefined;

    /** Called when the active state changes */
    onStateChanged(prev: string | undefined, next: string | undefined): void;

    /**
     * Apply a state transform override (engine-specific Vector3/Quaternion math).
     *
     * `enterFromOverride` is set when the object is transitioning inactive → active
     * for this recompute. The host should snap to `(target + enterFromOverride)`
     * before animating to the target pose. When undefined, normal "from-live" pose
     * interpolation applies.
     */
    applyStateTransform(
        state: StateDefinition | undefined,
        hadPrevious: boolean,
        transition: TransitionConfig | undefined,
        enterFromOverride?: TransformOverride,
    ): void;

    /**
     * Apply a state active override.
     *
     * `desiredActive` is what the top eligible state's `active` field resolves
     * to (true or false), or `undefined` when no state declares it (host should
     * revert to its authored value). The host shows and hides without writing
     * authored data.
     *
     * When `desiredActive === false`, the host should defer the hide until the
     * transition completes; `desiredActive === true` should show immediately so
     * the object is visible during entry animation.
     */
    applyStateActive(
        desiredActive: boolean | undefined,
        transition: TransitionConfig | undefined,
    ): void;

    /** Set up a property-driven transform (engine-specific) */
    setupTransformDriver(
        state: StateDefinition,
        config: PropertyTransition,
    ): void;

    /** Tear down any active transform driver */
    teardownTransformDriver(): void;

    /** Called when a driver value changes and __transform__ is a target */
    onTransformDriverValueChanged(driverName: string, driverValue: number): void;

    /**
     * Read the host's current displayed active state. Used to decide whether
     * an `inactive → active` transition is happening (which feeds `enterFrom`
     * into `applyStateTransform`).
     */
    getHostActive(): boolean;
}

/**
 * Pure Canopy resolution engine.
 *
 * Manages property declarations, value resolution, state stacking,
 * subscriptions, inheritance cascade, and property-driven transitions.
 * Delegates engine-specific operations to the host via callbacks and adapters.
 */
export class PropertyResolver implements CanopyProvider {
    /** Property declaration lookup by name */
    private _propertyMap: Map<string, PropertyDeclaration> = new Map();

    /** Subscription registry: property name → subscribers */
    private _subscribers: Map<string, Set<CanopySubscriber>> = new Map();

    // ── State stack ──

    /** The runtime slot: the custom state and pseudo-states that drivers set. */
    private _stateStack: ActiveStateEntry[] = [];

    /**
     * The editor's pick (CANOPY_STATES §8.4): a state name (`X`, `:hover`,
     * `X:hover`), null for Base, or undefined for none. While one is set it
     * decides what shows, as `_pickStack`, and the runtime slot keeps
     * updating underneath.
     */
    private _editorPick: string | null | undefined = undefined;
    private _pickStack: ActiveStateEntry[] = [];

    /** The stack that shows: the pick's, else the runtime slot. */
    private get _shown(): ActiveStateEntry[] {
        return this._editorPick === undefined ? this._stateStack : this._pickStack;
    }
    private _baseValues: Map<string, PropertyValue> = new Map();

    /**
     * External state contributions pushed by non-Properties sources. Keys are
     * `${contributorId}:${name}` so a single contributor can push multiple
     * named states (though Keyframes only ever pushes one).
     */
    private _contributions: Map<string, ContributionEntry> = new Map();
    private _contributionSeq = 0;

    /** Cached eligible states — invalidated on stack changes */
    private _eligibleStatesCache: StateDefinition[] | null = null;

    // ── Runtime overrides ──

    private _runtimeOverrides: Map<string, PropertyValue> = new Map();

    // ── Transition state ──

    private _transitionValues: Map<string, PropertyValue> = new Map();
    private _activeTransitionKeys: Set<string> = new Set();

    /**
     * Target value of each in-flight time transition, keyed by property. Lets
     * `setValue` implement the state-selection re-click mechanic: requesting the
     * value a property is *already* animating toward snaps instead of restarting.
     */
    private _transitionTargets: Map<string, PropertyValue> = new Map();

    /**
     * Opt-in completion barriers for the awaited exit phase (see
     * {@link setStateAwaited}). Each barrier watches a fixed set of property
     * keys that were transitioning when the barrier was installed; it resolves
     * once every watched key has drained (its time-driven transition completed
     * or was cancelled). Empty by default — no barrier exists unless a caller
     * opted into an awaited state change, so the immediate-revert semantics of
     * {@link setState} are completely untouched.
     */
    private _exitBarriers: Array<{ keys: Set<string>; resolve: () => void }> = [];

    /**
     * When non-null, collects the keys of every **clock-driven** (Transition
     * Manager) transition started during an awaited state change. Set up by
     * {@link setStateAwaited} around its recompute so the resulting barrier
     * watches exactly the keys this change (re)started — which correctly:
     *  - EXCLUDES property-driver keys (they never call `startTransition`, so
     *    they never drain and would hang the barrier), and
     *  - INCLUDES a restart of an already-active key (it calls `startTransition`
     *    again, whereas a before/after set-diff would miss it).
     */
    private _startedKeyCapture: Set<string> | null = null;

    // ── Property-driven transitions ──

    private _propertyDriverTargets: Map<string, PropertyDriverTarget> = new Map();
    private _propertyDriverSources: Map<string, Set<string>> = new Map();
    private _driverSubscriptions: Map<string, { provider: CanopyProvider; subscriber: CanopySubscriber }> = new Map();

    /** Driver names that resolved to system values (no PropertyProvider found) */
    private _systemDriverNames: Set<string> = new Set();

    /** Whether a state-driven transform override is currently applied */
    private _hasActiveTransformOverride = false;

    /** Whether a state-driven `active` (enabled) override is currently applied */
    private _hasActiveEnabledOverride = false;

    constructor(
        private _nodeAdapter: CanopyNodeAdapter,
        private _transitionHost: CanopyTransitionHost,
        private _callbacks: ResolverCallbacks,
        private _getAuthored: () => PropertiesData,
    ) {}

    // ── Property map ──

    /**
     * Rebuild the property lookup map from declarations.
     * Called by the host after loading sheets or committing changes.
     */
    rebuildPropertyMap(
        inlineDeclarations: PropertyDeclaration[],
        sheetDeclarations: PropertyDeclaration[],
    ): void {
        this._propertyMap.clear();

        // Sheet declarations first (lower precedence)
        for (const decl of sheetDeclarations) {
            if (!this._propertyMap.has(decl.name)) {
                this._propertyMap.set(decl.name, decl);
            }
        }

        // Inline declarations override sheets
        for (const decl of inlineDeclarations) {
            this._propertyMap.set(decl.name, decl);
        }
    }

    /** Get the current property map (for host access to declarations) */
    getPropertyMap(): Map<string, PropertyDeclaration> {
        return this._propertyMap;
    }

    // ── Value resolution (Canopy 5-step cascade) ──

    /**
     * Get a property value by name, in **canonical** units (metres).
     *
     * Resolution runs in the declaration's own declared space
     * ({@link _getDeclaredValue}); this is the one place where a `units` tag is
     * honoured, converting the result to metres so no consumer ever sees a unit.
     * See docs/canopy/CANOPY_UNITS.md.
     */
    getValue(name: string, settled = false): PropertyValue | undefined {
        return toCanonical(this._unitsOf(name), this._getDeclaredValue(name, settled));
    }

    /**
     * The units tag governing `name` on THIS node, i.e. the space its internal
     * values live in. Only `number` and `vec3` declarations carry length units;
     * every other type — and a property with no local declaration at all — is
     * canonical (factor 1). CANOPY_UNITS §2, §4.
     */
    private _unitsOf(name: string): Units | undefined {
        return declarationUnits(this._propertyMap.get(name));
    }

    /**
     * Get a property value in this node's DECLARED space (no unit conversion).
     *
     * Resolution order (Canopy spec):
     * 1. Transition overlay (mid-animation)
     * 2. Runtime override (setValue)
     * 3. Local state stack (highest-specificity eligible state)
     * 4. Ancestor value (if `inherits !== false`) — imported canonical, converted in
     * 5. Local declared value
     */
    private _getDeclaredValue(name: string, settled = false): PropertyValue | undefined {
        if (!this.hasProperty(name)) return undefined;

        // Steps 1-3 are the session's view; a base read skips them (readAtBase).
        if (baseReadDepth === 0) {
            // 1. Mid-transition interpolated value. A `settled` read skips it to get
            // the TARGET state value — e.g. an AnimationSequence resolving a slot's
            // clip src+name as an atomic pair instead of following the per-property
            // discrete flip (which lands a new src against the old name mid-transition).
            if (!settled) {
                const transitionValue = this._transitionValues.get(name);
                if (transitionValue !== undefined) return transitionValue;
            }

            // 2. Runtime override
            const override = this._runtimeOverrides.get(name);
            if (override !== undefined) return override;

            // 3. Local state stack (or external contributions)
            if (this._shown.length > 0 || this._contributions.size > 0) {
                const eligible = this._resolveEligibleStates();
                for (const stateDef of eligible) {
                    if (Object.hasOwn(stateDef.values, name) && stateDef.values[name] !== undefined) return stateDef.values[name];
                }
            }
        }

        const decl = this._propertyMap.get(name);

        // 4. Ancestor walk (if inherits)
        if (decl?.inherits !== false) {
            const ancestorVal = this._resolveAncestorValue(name);
            if (ancestorVal !== undefined) return ancestorVal;
        }

        // 5. Local value
        return decl?.value;
    }

    /**
     * Check if a property is available — declared locally or overridden in any state
     * (including external contributions).
     */
    hasProperty(name: string): boolean {
        if (this._propertyMap.has(name)) return true;
        const states = this._getAuthored().states;
        if (states) {
            for (const stateDef of Object.values(states)) {
                if (Object.hasOwn(stateDef.values, name)) return true;
            }
        }
        for (const contrib of this._contributions.values()) {
            if (Object.hasOwn(contrib.def.values, name)) return true;
        }
        return false;
    }

    /** Get a declaration by name */
    getProperty(name: string): PropertyDeclaration | undefined {
        return this._propertyMap.get(name);
    }

    /** Get all property names */
    getPropertyNames(): string[] {
        return Array.from(this._propertyMap.keys());
    }

    /** Get all declarations */
    getProperties(): PropertyDeclaration[] {
        return Array.from(this._propertyMap.values());
    }

    /** Get declarations with labels (for AutoUI) */
    getLabeledProperties(): PropertyDeclaration[] {
        return Array.from(this._propertyMap.values()).filter(p => p.label !== undefined);
    }

    // ── Internal value resolution (no transition overlay) ──

    /**
     * Get the current value without transition overlay, in this node's DECLARED
     * space — transition endpoints, captured display values and driver values are
     * all declared-space (CANOPY_UNITS §3). Public consumers want
     * {@link getValue}, which converts to canonical metres.
     *
     * Used internally for base value capture and driver resolution.
     */
    getCurrentValue(name: string): PropertyValue | undefined {
        const override = this._runtimeOverrides.get(name);
        if (override !== undefined) return override;

        // Include external contributions in the lookup — otherwise
        // _captureDisplayValues returns the declared base instead of the
        // currently-displayed contribution value, and time-driven transitions
        // (e.g. a keyframe switch) interpolate from the base rather than from
        // the prior keyframe's value.
        if (this._shown.length > 0 || this._contributions.size > 0) {
            const eligible = this._resolveEligibleStates();
            for (const stateDef of eligible) {
                if (Object.hasOwn(stateDef.values, name) && stateDef.values[name] !== undefined) return stateDef.values[name];
            }
        }

        const decl = this._propertyMap.get(name);
        if (decl?.inherits !== false) {
            const ancestorVal = this._resolveAncestorValue(name);
            if (ancestorVal !== undefined) return ancestorVal;
        }
        return decl?.value;
    }

    /**
     * Walk ancestors for the nearest computed value, converted into THIS node's
     * declared space. The ancestor hands back a canonical (metres) value —
     * `getValue` is the resolver's read boundary — so a local `units` tag has to
     * pull it back into the space the local cascade works in (CANOPY_UNITS §4).
     * With no local declaration, or on a non-length type, the factor is 1 and
     * the ancestor's canonical value passes through untouched.
     */
    private _resolveAncestorValue(propName: string): PropertyValue | undefined {
        for (const provider of this._nodeAdapter.walkAncestorProviders()) {
            if (provider.hasProperty(propName)) {
                const val = provider.getValue(propName);
                if (val !== undefined) return fromCanonical(this._unitsOf(propName), val);
            }
        }
        return undefined;
    }

    /** Declaration-default transition (VARIANTS_DESIGN §5): fallback when no initiator transition. */
    private _declTransition(name: string): TimeTransition | undefined {
        return this._propertyMap.get(name)?.transition;
    }

    // ── setValue (runtime override) ──

    /**
     * Set a runtime override for a property.
     * Returns false if the property is unknown or type-check fails.
     */
    setValue(name: string, value: PropertyValue, transition?: TransitionConfig): boolean {
        // Re-click mechanic (mirrors state selection): if a TIME transition toward
        // this exact value is already in flight, snap to it instead of restarting.
        // Property-driven transitions are excluded: they don't maintain
        // _transitionTargets (no fixed target / no completion callback), so a stale
        // entry left by a superseded time transition must not false-match here.
        let effectiveTransition = transition ?? this._declTransition(name);
        if (this._activeTransitionKeys.has(name)
            && !this._propertyDriverTargets.has(name)
            && PropertyResolver._valuesEqual(this._transitionTargets.get(name), value)) {
            effectiveTransition = undefined;
        }

        // Capture the currently-displayed value BEFORE installing the new override,
        // so a transition interpolates from what's on screen — a prior runtime
        // override or state/ancestor value — not from the declaration default.
        // (An active transition's live overlay wins.)
        const fromValue = this._transitionValues.get(name) ?? this.getCurrentValue(name);
        this._runtimeOverrides.set(name, value);
        return this._applyValue(name, value, effectiveTransition, fromValue);
    }

    /**
     * Apply a value: type-check, transition if needed, notify subscribers.
     *
     * `fromValue` is the pre-write displayed value to interpolate from; when
     * omitted it is resolved from the transition overlay / base / declaration.
     */
    private _applyValue(name: string, value: PropertyValue, transition?: TransitionConfig, fromValue?: PropertyValue): boolean {
        if (!this.hasProperty(name)) return false;

        const decl = this._propertyMap.get(name);
        if (decl && !PropertyResolver._typeCheck(decl.type, value)) return false;

        const previousValue = fromValue ?? this._transitionValues.get(name) ?? this._baseValues.get(name) ?? decl?.value ?? value;

        if (transition && isTimeTransition(transition) && PropertyResolver._timeTransitionApplies(transition)) {
            this._startTransition(name, previousValue, value, transition);
        } else {
            if (this._activeTransitionKeys.has(name)) {
                this._transitionHost.cancelTransition(name);
                this._activeTransitionKeys.delete(name);
                this._transitionTargets.delete(name);
                this._drainTransitionKey(name);
            }
            this._transitionValues.delete(name);
            this._notifySubscribers(name);
            this._cascadePropertyChange(name);
        }
        return true;
    }

    /**
     * Drop ALL runtime overrides (priority-2 inline-style writes) and re-resolve
     * the affected properties down to the state stack / authored value. Unlike a
     * commit — which keeps an override whose declaration value is unchanged — this
     * unconditionally clears them. Used to return to authored state, e.g. when the
     * editor exits play mode: `setValue` picks made during play must not survive.
     * Any in-flight transition on a cleared property is cancelled.
     */
    clearRuntimeOverrides(): void {
        if (this._runtimeOverrides.size === 0) return;
        const cleared = [...this._runtimeOverrides.keys()];
        this._runtimeOverrides.clear();
        for (const name of cleared) {
            if (this._activeTransitionKeys.has(name)) {
                this._transitionHost.cancelTransition(name);
                this._activeTransitionKeys.delete(name);
                this._transitionTargets.delete(name);
                this._drainTransitionKey(name);
            }
            this._transitionValues.delete(name);
            this._notifySubscribers(name);
            this._cascadePropertyChange(name);
        }
    }

    // ── Transition overlay ──

    /**
     * Set a transition overlay value directly. Used by external transition
     * systems (e.g. Pages match transitions).
     */
    setTransitionValue(name: string, value: PropertyValue): void {
        this._transitionValues.set(name, value);
        this._notifySubscribers(name);
        this._cascadePropertyChange(name);
    }

    /** Clear a transition overlay value */
    clearTransitionValue(name: string): void {
        this._transitionValues.delete(name);
        this._notifySubscribers(name);
        this._cascadePropertyChange(name);
    }

    /** Whether a property is currently transitioning */
    isTransitioning(name: string): boolean {
        return this._activeTransitionKeys.has(name);
    }

    /**
     * A transition key has drained (completed or cancelled). Remove it from
     * every pending exit barrier and resolve any barrier that has emptied.
     * Call this at every site that removes a key from `_activeTransitionKeys`.
     */
    private _drainTransitionKey(name: string): void {
        if (this._exitBarriers.length === 0) return;
        const settled: Array<() => void> = [];
        this._exitBarriers = this._exitBarriers.filter((b) => {
            b.keys.delete(name);
            if (b.keys.size === 0) {
                settled.push(b.resolve);
                return false;
            }
            return true;
        });
        for (const resolve of settled) resolve();
    }

    /** Number of active transitions */
    get activeTransitionCount(): number {
        return this._activeTransitionKeys.size;
    }

    // ── Time-driven transitions ──

    private _startTransition(
        name: string,
        fromValue: PropertyValue,
        toValue: PropertyValue,
        config: TimeTransition,
    ): void {
        this._transitionValues.set(name, fromValue);
        this._activeTransitionKeys.add(name);
        this._transitionTargets.set(name, toValue);
        this._startedKeyCapture?.add(name);

        this._startTransitionTicks(name, fromValue, toValue, config);

        this._notifySubscribers(name);
        this._cascadePropertyChange(name);
    }

    /**
     * Run a time transition's tick/complete pair — THE one implementation
     * shared by the setValue path (`_startTransition`) and the state path
     * (`_applyValueSilent`), so tick-behavior changes cannot diverge.
     * Callers must have seeded `_transitionValues`/`_activeTransitionKeys`/
     * `_transitionTargets` before calling.
     */
    private _startTransitionTicks(
        name: string,
        fromValue: PropertyValue,
        toValue: PropertyValue,
        config: TimeTransition,
    ): void {
        this._transitionHost.startTransition(
            name,
            config,
            (progress) => {
                // === 1: completion only — overshooting easings exceed 1 mid-flight
                const value = progress === 1
                    ? toValue
                    : interpolateValue(fromValue, toValue, progress);
                // Discrete values change displayed value at most twice per
                // transition (start, midpoint flip) — skip the per-tick
                // notify churn while the displayed value is unchanged.
                if (PropertyResolver._valuesEqual(this._transitionValues.get(name), value)) return;
                this._transitionValues.set(name, value);
                this._notifySubscribers(name);
                this._cascadePropertyChange(name);
            },
            () => {
                this._transitionValues.delete(name);
                this._activeTransitionKeys.delete(name);
                this._transitionTargets.delete(name);
                this._drainTransitionKey(name);
                this._notifySubscribers(name);
                this._cascadePropertyChange(name);
            },
        );
    }

    /** Cancel all active transitions */
    stopAllTransitions(): void {
        this.teardownAllPropertyDrivers();

        if (this._activeTransitionKeys.size === 0) return;
        const drained = [...this._activeTransitionKeys];
        const affected = new Set([...this._transitionValues.keys(), ...drained]);
        for (const name of this._activeTransitionKeys) {
            this._transitionHost.cancelTransition(name);
        }
        this._transitionValues.clear();
        this._activeTransitionKeys.clear();
        this._transitionTargets.clear();
        // Cancelling counts as draining — resolve any awaited exit barriers
        // rather than leaving their promises hanging forever.
        for (const name of drained) this._drainTransitionKey(name);
        // The overlay is gone — re-announce so subscribers drop the last
        // mid-flight value they were pushed and re-pull the resolved one.
        // Without this, a consumer with no follow-up commit (play-off outside
        // the editor) would keep displaying the frozen mid-fade value.
        for (const name of affected) {
            this._notifySubscribers(name);
            this._cascadePropertyChange(name);
        }
    }

    // ── State stack ──

    /** Get the top (highest-priority) state that shows: the editor's pick when one is set */
    getActiveState(): string | undefined {
        return this._shown.length > 0 ? this._shown[0].name : undefined;
    }

    /** Get all active state names, ordered by priority (highest first) */
    getActiveStates(): string[] {
        return this._shown.map(e => e.name);
    }

    /** The top state of the runtime slot, whatever the editor's pick shows. */
    getRuntimeState(): string | undefined {
        return this._stateStack.length > 0 ? this._stateStack[0].name : undefined;
    }

    /** The editor's pick: a state name, null for Base, undefined for none. */
    getEditorState(): string | null | undefined {
        return this._editorPick;
    }

    /**
     * Show the editor's pick over the runtime slot: a custom state, a
     * pseudo-state alone (`:hover`) or both (`X:hover`); null shows Base, and
     * undefined hands back to the runtime slot, as does a pick naming a state
     * this object doesn't have.
     */
    setEditorState(requested: string | null | undefined, instant = false): void {
        let entries = requested ? this._pickEntries(requested) : [];
        const pick = requested === '' || !entries ? undefined : requested;
        if (pick === this._editorPick) return;
        entries ??= [];
        const prev = this.getActiveState();
        const prevCustom = this._shown.find(e => !e.name.startsWith(':'))?.name;
        const prevValues = this._captureDisplayValues();
        this._editorPick = pick;
        this._pickStack = entries;
        this._invalidateEligibleStatesCache();
        const initiating = entries.length > 0 ? entries[entries.length - 1].name : prevCustom;
        this._recomputeFromStack(instant, prev, prevValues, undefined, initiating);
    }

    /** A pick's stack entries, highest priority first; null when it names a state this object lacks. */
    private _pickEntries(pick: string): ActiveStateEntry[] | null {
        const colon = pick.indexOf(':');
        const custom = colon < 0 ? pick : pick.substring(0, colon);
        const pseudo = colon < 0 ? undefined : pick.substring(colon);
        if (custom && !this.hasState(custom)) return null;
        if (pseudo && !this._hasPseudoReference(pseudo)) return null;
        const entries: ActiveStateEntry[] = [];
        if (pseudo) entries.push({ name: pseudo, priority: PSEUDO_STATE_PRIORITY[pseudo] ?? CUSTOM_STATE_PRIORITY });
        if (custom) entries.push({ name: custom, priority: CUSTOM_STATE_PRIORITY });
        return entries;
    }

    /** Check if a named state is defined */
    hasState(name: string): boolean {
        return !!this._getAuthored().states?.[name];
    }

    /** Get the transition config for a named state */
    getStateTransition(name: string): TransitionConfig | undefined {
        return this._getAuthored().states?.[name]?.transition;
    }

    /** Get all defined state names */
    getStateNames(): string[] {
        const authored = this._getAuthored();
        if (!authored.states) return [];
        return Object.keys(authored.states);
    }

    /** Check if any state definition references a pseudo-state */
    hasPseudoStates(): boolean {
        return this.getStateNames().some(n => n.includes(':'));
    }

    /**
     * Set a custom (non-pseudo) state. Replaces any existing custom state.
     * Pass undefined to clear.
     */
    setState(name: string | undefined, instant = false, transitionFallback?: TransitionConfig): void {
        if (name !== undefined && !this.hasState(name)) return;

        const existingCustomIdx = this._stateStack.findIndex(e => !e.name.startsWith(':'));

        // R7 idempotence (STATE_SCOPE_SPEC.md §3): writing the custom slot to its
        // current value is a STRICT no-op — no splice/recompute, no transition
        // restart, no onStateChange emission. This is what makes a scoped setState
        // safe to re-apply across every group member (and legacy baked chains
        // re-set the same value harmlessly). Base(undefined) matches "no custom
        // state on the stack"; a name matches the current custom entry's name.
        const currentCustom = existingCustomIdx < 0 ? undefined : this._stateStack[existingCustomIdx].name;
        if (name === currentCustom) return;

        if (existingCustomIdx < 0) {
            // name is necessarily defined here: name === undefined with no custom
            // entry equals currentCustom === undefined, already handled by R7.
            this._pushToStack(name!, CUSTOM_STATE_PRIORITY, instant, transitionFallback);
            return;
        }

        // Replace (or clear) the existing custom state atomically: capture the
        // displayed values BEFORE mutating the stack, then do remove + insert in
        // a single recompute. A remove followed by _pushToStack would re-capture
        // after the old state left the stack, so the transition "from" values
        // resolve to base and the switch visibly passes through the base state
        // (same bug class replaceContribution exists to prevent for keyframes).
        const prev = this.getActiveState();
        const prevValues = this._captureDisplayValues();
        this._stateStack.splice(existingCustomIdx, 1);
        this._invalidateEligibleStatesCache();
        if (name !== undefined) {
            this._insertStackEntry(name, CUSTOM_STATE_PRIORITY);
        }
        // Initiator: the entered state, or the removed one when clearing
        this._recomputeFromStack(instant, prev, prevValues, transitionFallback, name ?? currentCustom);
    }

    /**
     * Awaited variant of {@link setState}: applies the same state change, then
     * returns a Promise that resolves once every property transition this change
     * *started* has completed (or was cancelled/interrupted). This is the shared
     * "awaited state exit phase" primitive (Pages spec P-18) — it lets a caller
     * sequence work after a state's transition plays out (e.g. defer a page flip
     * until the outgoing page's exit finishes) without reimplementing timing.
     *
     * Opt-in and additive: `setState` is unchanged and callers that don't await
     * see identical behavior. If the change starts no time-driven transitions
     * (instant change, no interpolable properties, zero duration), the returned
     * Promise is already resolved.
     *
     * Note: only **clock-driven** property transitions are awaited here.
     * Property-DRIVEN transitions (`{driver}`) are excluded — they have no
     * completion clock, so awaiting one would hang; a state that deactivates via
     * a driver resolves this Promise immediately. Transform-only exits (a state
     * that changes pose but no properties) complete on the host's transition
     * clock; the host layer (Properties) folds those — and any bound exit clip —
     * into the awaited signal it exposes.
     */
    setStateAwaited(name: string | undefined, instant = false, transitionFallback?: TransitionConfig): Promise<void> {
        // Capture exactly the clock-driven transitions this change (re)starts.
        // Using a capture set rather than a before/after diff of active keys
        // (a) excludes property-driver keys — they never call the transition
        // host's startTransition, so they never drain, and (b) includes a
        // RESTART of an already-active key, which a set-diff would miss and
        // resolve prematurely.
        const started = new Set<string>();
        const prevCapture = this._startedKeyCapture;
        this._startedKeyCapture = started;
        try {
            this.setState(name, instant, transitionFallback);
        } finally {
            this._startedKeyCapture = prevCapture;
        }
        // A key may have started then immediately drained within setState (e.g.
        // a zero-duration snap on a later recompute) — keep only keys still live.
        for (const key of [...started]) {
            if (!this._activeTransitionKeys.has(key)) started.delete(key);
        }
        if (started.size === 0) return Promise.resolve();
        return new Promise<void>((resolve) => {
            this._exitBarriers.push({ keys: started, resolve });
        });
    }

    /** Clear the editor's pick, the custom state and every pseudo-state at once, instantly: Base, plus any contributions. */
    resetStates(): void {
        if (this._stateStack.length === 0 && this._editorPick === undefined) return;
        const prev = this.getActiveState();
        this._stateStack.length = 0;
        this._editorPick = undefined;
        this._pickStack = [];
        this._invalidateEligibleStatesCache();
        this._recomputeFromStack(true, prev);
    }

    /**
     * Activate or deactivate a pseudo-state.
     */
    setPseudoState(name: string, active: boolean, instant = false): void {
        if (active) {
            if (!this._hasPseudoReference(name)) return;
            if (this._stateStack.some(e => e.name === name)) return;
            const priority = PSEUDO_STATE_PRIORITY[name] ?? CUSTOM_STATE_PRIORITY;
            this._pushToStack(name, priority, instant);
        } else {
            this._removeFromStack(name, instant);
        }
    }

    /** Check if a pseudo-state name is referenced by any state definition */
    hasPseudoReference(pseudo: string): boolean {
        return this._hasPseudoReference(pseudo);
    }

    private _hasPseudoReference(pseudo: string): boolean {
        const states = this._getAuthored().states;
        if (!states) return false;
        for (const name of Object.keys(states)) {
            if (name === pseudo) return true;
            const colonIdx = name.indexOf(':');
            if (colonIdx > 0 && name.substring(colonIdx) === pseudo) return true;
        }
        return false;
    }

    // ── External state contributions ──

    /**
     * Push (or replace) an external state contribution. The contribution
     * participates in the state stack at the custom-state tier (10); later
     * pushes win ties at the same tier.
     *
     * `instant` snaps to the new resolved values without animating.
     * `transitionFallback` is used when neither the contribution nor any other
     * eligible state declares a transition.
     */
    pushContribution(
        contributorId: number,
        name: string,
        def: StateDefinition,
        instant = false,
        transitionFallback?: TransitionConfig,
    ): void {
        const key = `${contributorId}:${name}`;
        const prev = this.getActiveState();
        const prevValues = this._captureDisplayValues();
        this._contributions.set(key, {
            contributorId,
            name,
            def,
            seq: ++this._contributionSeq,
        });
        this._invalidateEligibleStatesCache();
        this._recomputeFromStack(instant, prev, prevValues, transitionFallback, name);
    }

    /**
     * Clear a specific contribution. No-op if not present.
     */
    clearContribution(
        contributorId: number,
        name: string,
        instant = false,
        transitionFallback?: TransitionConfig,
    ): void {
        const key = `${contributorId}:${name}`;
        if (!this._contributions.has(key)) return;
        const prev = this.getActiveState();
        const prevValues = this._captureDisplayValues();
        this._contributions.delete(key);
        this._invalidateEligibleStatesCache();
        this._recomputeFromStack(instant, prev, prevValues, transitionFallback, name);
    }

    /**
     * Atomically replace one contribution with another. Used by Keyframes when
     * switching between two keyframes that both target the same path: a
     * clearContribution + pushContribution sequence would trigger two
     * recomputes — the first reverts to authored (snapping the visible pose),
     * the second animates from authored to the new target. Doing both
     * mutations in one recompute lets the resolver see the swap as a single
     * state transition and interpolate directly from the current visible pose
     * to the new target.
     *
     * If `oldName === newName` this is equivalent to pushContribution.
     */
    replaceContribution(
        contributorId: number,
        oldName: string,
        newName: string,
        def: StateDefinition,
        instant = false,
        transitionFallback?: TransitionConfig,
    ): void {
        const oldKey = `${contributorId}:${oldName}`;
        const newKey = `${contributorId}:${newName}`;
        const prev = this.getActiveState();
        const prevValues = this._captureDisplayValues();
        if (oldKey !== newKey) this._contributions.delete(oldKey);
        this._contributions.set(newKey, {
            contributorId,
            name: newName,
            def,
            seq: ++this._contributionSeq,
        });
        this._invalidateEligibleStatesCache();
        this._recomputeFromStack(instant, prev, prevValues, transitionFallback, newName);
    }

    /**
     * Clear all contributions from a given contributor. Used by Keyframes on
     * detach or when navigating to a keyframe that no longer affects a path.
     */
    clearAllContributions(
        contributorId: number,
        instant = false,
        transitionFallback?: TransitionConfig,
    ): void {
        let removed = false;
        const prev = this.getActiveState();
        const prevValues = this._captureDisplayValues();
        for (const [key, contrib] of this._contributions) {
            if (contrib.contributorId === contributorId) {
                this._contributions.delete(key);
                removed = true;
            }
        }
        if (!removed) return;
        this._invalidateEligibleStatesCache();
        this._recomputeFromStack(instant, prev, prevValues, transitionFallback);
    }

    /**
     * Apply initial state on play mode start.
     * Time-driven initial states snap instantly (no entry animation) — the intent is
     * to set up the starting state, not animate from base. Property-driven initial
     * states activate normally so the driver can set initial progress.
     */
    applyInitialState(): void {
        const initial = this._getAuthored().initialState;
        if (initial && this.hasState(initial)) {
            const transition = this._getAuthored().states?.[initial]?.transition;
            const instant = !transition || !isPropertyTransition(transition);
            this.setState(initial, instant);
        }
    }

    // ── Stack internals ──

    private _pushToStack(name: string, priority: number, instant = false, transitionFallback?: TransitionConfig): void {
        const prev = this.getActiveState();
        const prevValues = this._captureDisplayValues();
        this._insertStackEntry(name, priority);
        this._recomputeFromStack(instant, prev, prevValues, transitionFallback, name);
    }

    /** Insert a stack entry in priority order (no capture, no recompute). */
    private _insertStackEntry(name: string, priority: number): void {
        const entry: ActiveStateEntry = { name, priority };
        let inserted = false;
        for (let i = 0; i < this._stateStack.length; i++) {
            if (priority > this._stateStack[i].priority) {
                this._stateStack.splice(i, 0, entry);
                inserted = true;
                break;
            }
        }
        if (!inserted) this._stateStack.push(entry);
        this._invalidateEligibleStatesCache();
    }

    private _removeFromStack(name: string, instant = false): void {
        const idx = this._stateStack.findIndex(e => e.name === name);
        if (idx < 0) return;
        const prev = this.getActiveState();
        const prevValues = this._captureDisplayValues();
        this._stateStack.splice(idx, 1);
        this._invalidateEligibleStatesCache();
        this._recomputeFromStack(instant, prev, prevValues, undefined, name);
    }

    /**
     * Resolve eligible states ordered by CSS-like specificity.
     * Returns StateDefinition[] in descending specificity order.
     * Result is cached — invalidated when the state stack or contributions change.
     */
    private _resolveEligibleStates(): StateDefinition[] {
        if (this._eligibleStatesCache) return this._eligibleStatesCache;
        const authored = this._getAuthored();

        const activeCustom = this._shown.find(e => !e.name.startsWith(':'))?.name;
        const activePseudos = new Set(this._shown.filter(e => e.name.startsWith(':')).map(e => e.name));

        const compounds: StateDefinition[] = [];
        const pseudos: Array<{ priority: number; def: StateDefinition }> = [];
        const customs: StateDefinition[] = [];

        if (authored.states) {
            for (const [name, def] of Object.entries(authored.states)) {
                if (name === 'default') continue;
                const colonIdx = name.indexOf(':');

                if (colonIdx > 0) {
                    const customPart = name.substring(0, colonIdx);
                    const pseudoPart = name.substring(colonIdx);
                    if (customPart === activeCustom && activePseudos.has(pseudoPart)) {
                        compounds.push(def);
                    }
                } else if (colonIdx === 0) {
                    if (activePseudos.has(name)) {
                        const entry = this._shown.find(e => e.name === name);
                        pseudos.push({ priority: entry?.priority ?? 0, def });
                    }
                } else {
                    if (name === activeCustom) customs.push(def);
                }
            }
        }

        // External contributions: sort by seq descending so later pushes win ties
        // when stacked at the same (custom) priority tier.
        const contribs: StateDefinition[] = [];
        if (this._contributions.size > 0) {
            const sorted = Array.from(this._contributions.values()).sort((a, b) => b.seq - a.seq);
            for (const c of sorted) contribs.push(c.def);
        }

        pseudos.sort((a, b) => b.priority - a.priority);
        // Order: compound pseudos > pseudos > customs (authored) > contributions.
        // Contributions slot below authored customs so an authored state can
        // override a Keyframe contribution at the same node.
        const result = [...compounds, ...pseudos.map(p => p.def), ...customs, ...contribs];
        this._eligibleStatesCache = result;
        return result;
    }

    /**
     * The transition driving a stack change belongs to the state that
     * initiated it — INCLUDING the compound def it participates in with the
     * currently active states: a hover-enter on an active custom state is
     * driven by `custom:hover` when authored (and a hover-leave by the same
     * compound it was driving — the pseudo is already off the stack by then).
     * Most specific candidate first, mirroring eligibility order.
     */
    private _resolveInitiatingTransition(initiating: string): TransitionConfig | undefined {
        const states = this._getAuthored().states;
        if (!states) return undefined;
        const activeCustom = this._shown.find(e => !e.name.startsWith(':'))?.name;
        const activePseudos = this._shown.filter(e => e.name.startsWith(':')).map(e => e.name);

        const candidates: string[] = [];
        if (initiating.startsWith(':')) {
            // Pseudo moments inherit DOWN the family: compound, the pseudo
            // itself, then the active custom state's transition (its
            // class-level default — CSS analogy: a transition on .card also
            // paces .card:hover enter/leave when :hover declares none).
            if (activeCustom) candidates.push(`${activeCustom}${initiating}`);
            candidates.push(initiating);
            if (activeCustom) candidates.push(activeCustom);
        } else {
            // Custom switches never inherit UP from pseudos — that's the
            // hijack this method exists to prevent.
            for (const p of activePseudos) candidates.push(`${initiating}${p}`);
            candidates.push(initiating);
        }
        for (const c of candidates) {
            const t = states[c]?.transition;
            if (t) return t;
        }
        return undefined;
    }

    /** Invalidate the eligible states cache (call after any stack mutation) */
    private _invalidateEligibleStatesCache(): void {
        this._eligibleStatesCache = null;
    }

    /**
     * Capture the current displayed value for every known property.
     * Called BEFORE the stack is modified so transition "from" values are correct.
     */
    private _captureDisplayValues(): Map<string, PropertyValue> {
        const values = new Map<string, PropertyValue>();
        for (const [name] of this._propertyMap) {
            const val = this._transitionValues.get(name) ?? this.getCurrentValue(name);
            if (val !== undefined) values.set(name, val);
        }
        const authored = this._getAuthored();
        if (authored.states) {
            for (const stateDef of Object.values(authored.states)) {
                for (const propName of Object.keys(stateDef.values)) {
                    if (!values.has(propName)) {
                        const val = this._transitionValues.get(propName) ?? this.getCurrentValue(propName);
                        if (val !== undefined) values.set(propName, val);
                    }
                }
            }
        }
        return values;
    }

    /**
     * Recompute all property values and transforms from the current stack.
     */
    private _recomputeFromStack(
        instant: boolean,
        prevTopState: string | undefined,
        prevDisplayValues?: Map<string, PropertyValue>,
        transitionFallback?: TransitionConfig,
        initiating?: string,
    ): void {
        const authored = this._getAuthored();
        const snapTransition: TimeTransition = { duration: 0 };

        // Notify state change
        const topState = this.getActiveState();
        if (topState !== prevTopState) {
            this._callbacks.onStateChanged(prevTopState, topState);
        }

        const eligible = this._resolveEligibleStates();

        // Determine transition config. The switch transition belongs to the
        // INITIATING state — the one entered (or left, on deactivate) — not to
        // whichever active state happens to sit highest in the stack with a
        // transition: a :hover pseudo layered above the custom state must not
        // hijack a tap-triggered state change's easing (its own transition
        // still drives its own enter/leave, where the pseudo IS the
        // initiator). Fallbacks unchanged: previous top state, then caller's.
        const prevStateDef = prevTopState ? authored.states?.[prevTopState] : undefined;
        const initiatingTransition = initiating ? this._resolveInitiatingTransition(initiating) : undefined;
        const stateTransition = instant ? snapTransition : (initiatingTransition ?? prevStateDef?.transition ?? transitionFallback);

        // Collect effective values per property
        const effectiveOverrides = new Map<string, { value: PropertyValue; transition?: TransitionConfig }>();
        for (const stateDef of eligible) {
            for (const [propName, value] of Object.entries(stateDef.values)) {
                if (!effectiveOverrides.has(propName)) {
                    effectiveOverrides.set(propName, { value, transition: stateDef.transition });
                }
            }
        }

        // Capture base values for newly overridden properties
        for (const [propName] of effectiveOverrides) {
            if (!this._baseValues.has(propName)) {
                const decl = this._propertyMap.get(propName);
                const baseVal = (decl?.inherits !== false ? this._resolveAncestorValue(propName) : undefined)
                    ?? decl?.value;
                if (baseVal !== undefined) this._baseValues.set(propName, baseVal);
            }
        }

        // Tear down drivers whose owning state no longer contributes
        for (const [targetName, target] of this._propertyDriverTargets) {
            const override = effectiveOverrides.get(targetName);
            if (!override) {
                this._teardownPropertyDriver(targetName);
            } else if (override.transition !== undefined
                && isPropertyTransition(override.transition)
                && override.transition.driver === target.config.driver) {
                // Same driver still winning — keep
            } else if (!override.transition || !isPropertyTransition(override.transition)) {
                const driverStateStillActive = eligible.some(s =>
                    s.transition && isPropertyTransition(s.transition) && s.transition.driver === target.config.driver
                    && Object.hasOwn(s.values, targetName) && s.values[targetName] !== undefined
                );
                if (!driverStateStillActive) this._teardownPropertyDriver(targetName);
            }
        }

        // Apply all values (batch before notifying)
        const changedProps: string[] = [];
        for (const [propName, { value, transition: propTransition }] of effectiveOverrides) {
            this._runtimeOverrides.delete(propName);
            const hasExistingDriver = this._propertyDriverTargets.has(propName);
            const t = (hasExistingDriver && propTransition && isPropertyTransition(propTransition))
                ? propTransition
                : (stateTransition ?? this._declTransition(propName));
            this._applyValueSilent(propName, value, t, prevDisplayValues?.get(propName));
            changedProps.push(propName);
        }

        // Revert properties no longer overridden
        const timeRevert = stateTransition && isTimeTransition(stateTransition) ? stateTransition : undefined;
        const revertTransition = instant ? snapTransition : (transitionFallback ?? timeRevert);
        for (const [propName, baseValue] of this._baseValues) {
            if (effectiveOverrides.has(propName)) continue;
            this._applyValueSilent(propName, baseValue, revertTransition ?? this._declTransition(propName), prevDisplayValues?.get(propName));
            changedProps.push(propName);
            this._baseValues.delete(propName);
        }

        // Batch notify
        for (const propName of changedProps) {
            this._notifySubscribers(propName);
            this._cascadePropertyChange(propName);
        }

        // Active / enterFrom: first eligible state with the field wins.
        // Transform: resolved PER-COMPONENT (position/rotation/scale independently)
        // — the highest-priority eligible state that sets a component wins it, and
        // unset components fall back to authored. This mirrors how property VALUES
        // stack across states (each property resolves independently down the stack),
        // and is well-defined now that transforms are ABSOLUTE poses (no delta
        // composition). So `:hover { scale }` over `State 1 { rotation }` yields
        // hover's scale + State 1's rotation + authored position. (CANOPY_STATES §5.2.)
        let topActiveState: StateDefinition | undefined;
        let topEnterFromState: StateDefinition | undefined;
        // `topTransformState` = the highest-priority state owning ANY transform
        // facet — drives the transform DRIVER selection (a per-component driver is
        // out of scope; the discrete merged value below covers the common case).
        let topTransformState: StateDefinition | undefined;
        // Components pass straight through to the host's _computeTransformTargets,
        // which is what resolves a bound component to a number — so these carry
        // TransformOverride's own component type, not a numeric one.
        let mPos: TransformOverride['position'];
        let mRot: TransformOverride['rotation'];
        let mScl: TransformOverride['scale'];
        for (const stateDef of eligible) {
            const tf = stateDef.transform;
            if (tf) {
                if (!topTransformState) topTransformState = stateDef;
                if (!mPos && tf.position) mPos = tf.position;
                if (!mRot && tf.rotation) mRot = tf.rotation;
                if (!mScl && tf.scale) mScl = tf.scale;
            }
            if (!topActiveState && stateDef.active !== undefined) topActiveState = stateDef;
            if (!topEnterFromState && stateDef.enterFrom) topEnterFromState = stateDef;
            // Every accumulator is "highest-priority wins" (first set sticks), so
            // once all are filled the remaining lower-priority states can't change
            // anything. (topTransformState is set whenever any of mPos/mRot/mScl is.)
            if (mPos && mRot && mScl && topActiveState && topEnterFromState) break;
        }
        // The merged absolute transform (omitted components → authored, handled by
        // the host's _computeTransformTargets). Undefined when no state sets any.
        const mergedTransform: TransformOverride | undefined =
            (mPos || mRot || mScl)
                ? { ...(mPos ? { position: mPos } : {}), ...(mRot ? { rotation: mRot } : {}), ...(mScl ? { scale: mScl } : {}) }
                : undefined;

        // Active: applied before transform so the object becomes visible
        // before transform interpolation starts.
        const wasActiveOverrideApplied = this._hasActiveEnabledOverride;
        const desiredActive = topActiveState?.active;
        // enterFrom only fires on a transition from "currently not visible" → now visible.
        // Read the host's pre-recompute active state so we know whether the object
        // was visible BEFORE this state change. Capture this before applyStateActive
        // flips the host's active.
        const wasHostVisible = this._callbacks.getHostActive();
        const isBecomingVisible = !wasHostVisible && desiredActive === true;
        const enterFromForTransform = (isBecomingVisible && topEnterFromState) ? topEnterFromState.enterFrom : undefined;
        if (desiredActive !== undefined) {
            this._callbacks.applyStateActive(desiredActive, stateTransition);
        } else if (wasActiveOverrideApplied) {
            // No state declares active anymore — revert
            this._callbacks.applyStateActive(undefined, stateTransition);
        }
        this._hasActiveEnabledOverride = desiredActive !== undefined;

        // Handle transform driver vs time-driven
        const hasExistingTransformDriver = topTransformState?.transition && isPropertyTransition(topTransformState.transition);
        if (hasExistingTransformDriver && topTransformState) {
            const config = topTransformState.transition as PropertyTransition;
            this._callbacks.setupTransformDriver(topTransformState, config);

            // Ensure driver subscription
            const driverName = config.driver;
            if (!this._driverSubscriptions.has(driverName)) {
                this._subscribeToDriver(driverName);
            }
            let targets = this._propertyDriverSources.get(driverName);
            if (!targets) {
                targets = new Set();
                this._propertyDriverSources.set(driverName, targets);
            }
            targets.add('__transform__');
        } else {
            this._callbacks.teardownTransformDriver();
            const transformTransition = stateTransition;
            // Pass the PER-COMPONENT merged transform (not a single state's), so a
            // high-priority pseudo-state contributing only `scale` doesn't drop a
            // lower state's `rotation`. The host resolves omitted components against
            // authored. A synthetic StateDefinition carries the merged transform.
            const mergedState: StateDefinition | undefined =
                mergedTransform !== undefined ? { values: {}, transform: mergedTransform } : undefined;
            this._callbacks.applyStateTransform(
                mergedState,
                this._hasActiveTransformOverride,
                transformTransition,
                enterFromForTransform,
            );
        }
        this._hasActiveTransformOverride = topTransformState !== undefined;
    }

    /**
     * Apply a value silently (no notification — batched by caller).
     */
    private _applyValueSilent(
        name: string,
        value: PropertyValue,
        transition?: TransitionConfig,
        prevDisplayValue?: PropertyValue,
    ): void {
        if (!this.hasProperty(name)) return;
        const decl = this._propertyMap.get(name);
        if (decl && !PropertyResolver._typeCheck(decl.type, value)) return;

        const propType = decl?.type ?? PropertyResolver._inferType(value);

        if (transition && isPropertyTransition(transition) && PropertyResolver._isInterpolable(propType)) {
            // Property-driven transitions anchor fromValue to the settled base value
            // (declaration initialValue or inherited ancestor value). Using the
            // currently-displayed value would drift on re-activation: e.g. exiting
            // play mode mid-lerp and re-entering would use the mid-lerp color as
            // the new "from", so the driver's progress=0 end no longer points at
            // the declared initialValue. For property-driven, the endpoints are
            // fixed anchors, not "wherever we happen to be."
            const baseFrom = this._baseValues.get(name) ?? decl?.value ?? value;
            this._setupPropertyDriver(name, baseFrom, value, transition);
        } else if (transition && isTimeTransition(transition) && PropertyResolver._timeTransitionApplies(transition)) {
            const previousValue = this._transitionValues.get(name) ?? prevDisplayValue ?? this._baseValues.get(name) ?? decl?.value ?? value;
            this._transitionValues.set(name, previousValue);
            this._activeTransitionKeys.add(name);
            // Track the target here too (not just in _startTransition): a state
            // switch can REPLACE an in-flight setValue transition on the same key,
            // in which case the prior transition's onComplete never fires and its
            // stale _transitionTargets entry would linger — causing setValue's
            // re-click-snap check to false-positive later. Overwriting it here
            // keeps the map pointing at the actually-animating target.
            this._transitionTargets.set(name, value);
            this._startedKeyCapture?.add(name);

            this._startTransitionTicks(name, previousValue, value, transition);
        } else {
            // Snap
            if (this._activeTransitionKeys.has(name) && !this._propertyDriverTargets.has(name)) {
                this._transitionHost.cancelTransition(name);
                this._activeTransitionKeys.delete(name);
                this._transitionTargets.delete(name);
                this._drainTransitionKey(name);
            }
            this._transitionValues.delete(name);
        }
    }

    // ── Property-driven transitions ──

    private _setupPropertyDriver(
        targetName: string,
        fromValue: PropertyValue,
        toValue: PropertyValue,
        config: PropertyTransition,
    ): void {
        const driverName = config.driver;

        // A driver taking over this key obsoletes any time-transition target left
        // in _transitionTargets (drivers don't maintain it and have no completion
        // callback to clear it). Drop it so setValue's re-click-snap check can't
        // false-match a stale time target while the driver is active.
        this._transitionTargets.delete(targetName);

        // Circular check — catches direct self-reference and one level of chaining.
        // Deeper cycles (a→b→c→a) are prevented by the settled-value resolution
        // in _resolveDriverValue (which excludes transition overlays).
        if (driverName === targetName || this._propertyDriverTargets.has(driverName)) {
            this._transitionValues.set(targetName, toValue);
            return;
        }

        // Preserve existing matching driver
        const existing = this._propertyDriverTargets.get(targetName);
        if (existing && existing.config.driver === driverName
            && existing.config.range?.[0] === config.range?.[0]
            && existing.config.range?.[1] === config.range?.[1]
            && existing.config.easing === config.easing) {
            const driverValue = this._resolveDriverValue(driverName);
            const progress = computeDriverProgress(driverValue, config.range, config.easing);
            const interpolated = progress === 1 ? existing.toValue : progress === 0 ? existing.fromValue
                : interpolateValue(existing.fromValue, existing.toValue, progress);
            this._transitionValues.set(targetName, interpolated);
            return;
        }

        this._teardownPropertyDriver(targetName);

        this._propertyDriverTargets.set(targetName, { config, fromValue, toValue });

        let targets = this._propertyDriverSources.get(driverName);
        if (!targets) {
            targets = new Set();
            this._propertyDriverSources.set(driverName, targets);
        }
        targets.add(targetName);

        if (!this._driverSubscriptions.has(driverName)) {
            this._subscribeToDriver(driverName);
        }

        const driverValue = this._resolveDriverValue(driverName);
        const progress = computeDriverProgress(driverValue, config.range, config.easing);
        const interpolated = progress === 1 ? toValue : progress === 0 ? fromValue : interpolateValue(fromValue, toValue, progress);
        this._transitionValues.set(targetName, interpolated);
        this._activeTransitionKeys.add(targetName);
    }

    private _teardownPropertyDriver(targetName: string): void {
        const target = this._propertyDriverTargets.get(targetName);
        if (!target) return;

        const driverName = target.config.driver;
        this._propertyDriverTargets.delete(targetName);

        const targets = this._propertyDriverSources.get(driverName);
        if (targets) {
            targets.delete(targetName);
            if (targets.size === 0) {
                this._propertyDriverSources.delete(driverName);
                this._unsubscribeFromDriver(driverName);
            }
        }
    }

    /** Tear down ALL property drivers (properties + transform) */
    teardownAllPropertyDrivers(): void {
        for (const driverName of [...this._driverSubscriptions.keys()]) {
            this._unsubscribeFromDriver(driverName);
        }
        this._systemDriverNames.clear();
        this._propertyDriverTargets.clear();
        this._propertyDriverSources.clear();
        this._callbacks.teardownTransformDriver();
    }

    /**
     * Called when a driver value changes. Recomputes all targets.
     * Public so the host can call it for system-value driver polling.
     */
    onDriverValueChanged(driverName: string): void {
        const targets = this._propertyDriverSources.get(driverName);
        if (!targets || targets.size === 0) return;

        const driverValue = this._resolveDriverValue(driverName);
        const changedProps: string[] = [];

        for (const targetName of targets) {
            if (targetName === '__transform__') {
                this._callbacks.onTransformDriverValueChanged(driverName, driverValue);
                continue;
            }

            const target = this._propertyDriverTargets.get(targetName);
            if (!target) continue;

            const progress = computeDriverProgress(driverValue, target.config.range, target.config.easing);
            const value = progress === 1
                ? target.toValue
                : progress === 0
                    ? target.fromValue
                    : interpolateValue(target.fromValue, target.toValue, progress);
            this._transitionValues.set(targetName, value);
            changedProps.push(targetName);
        }

        for (const name of changedProps) {
            this._notifySubscribers(name);
            this._cascadePropertyChange(name);
        }
    }

    /**
     * Resolve the current driver value.
     * Uses settled value chain (no transition overlay) to avoid circular feedback.
     * Settled = the contribution/state target, not the in-flight interpolation —
     * so an active contribution (e.g. a keyframe) can drive, which is intended.
     */
    resolveDriverValue(driverName: string): number {
        return this._resolveDriverValue(driverName);
    }

    private _resolveDriverValue(driverName: string): number {
        const localValue = this.getCurrentValue(driverName);
        if (typeof localValue === 'number') return localValue;

        const ancestorValue = this._resolveAncestorValue(driverName);
        if (typeof ancestorValue === 'number') return ancestorValue;

        const exprValue = this._callbacks.resolveDriverExpression(driverName);
        if (typeof exprValue === 'number') return exprValue;

        return NaN;
    }

    /** Get the set of system driver names (for host polling) */
    getSystemDriverNames(): ReadonlySet<string> {
        return this._systemDriverNames;
    }

    /** Get the driver sources map (for host transform driver tracking) */
    getPropertyDriverSources(): ReadonlyMap<string, ReadonlySet<string>> {
        return this._propertyDriverSources;
    }

    private _subscribeToDriver(driverName: string): void {
        const provider = this._findDriverProvider(driverName);

        if (!provider) {
            this._systemDriverNames.add(driverName);
            return;
        }

        const subscriber: CanopySubscriber = {
            _onPropertyChanged: (name: string) => {
                if (name === driverName) this.onDriverValueChanged(driverName);
            },
            _resubscribeAndResolve: () => {
                this._unsubscribeFromDriver(driverName);
                if (this._propertyDriverSources.has(driverName)) {
                    this._subscribeToDriver(driverName);
                    this.onDriverValueChanged(driverName);
                }
            },
        };

        provider.subscribe(driverName, subscriber);
        this._driverSubscriptions.set(driverName, { provider, subscriber });
    }

    private _unsubscribeFromDriver(driverName: string): void {
        const sub = this._driverSubscriptions.get(driverName);
        if (sub) {
            sub.provider.unsubscribe(driverName, sub.subscriber);
            this._driverSubscriptions.delete(driverName);
        }
        this._systemDriverNames.delete(driverName);
    }

    private _findDriverProvider(driverName: string): CanopyProvider | null {
        if (this.hasProperty(driverName)) return this;
        for (const provider of this._nodeAdapter.walkAncestorProviders()) {
            if (provider.hasProperty(driverName)) return provider;
        }
        return null;
    }

    // ── Commit handling ──

    /**
     * Handle a commit — rebuild property map, clear stale overrides,
     * reset states, notify subscribers.
     */
    handleCommit(
        inlineDeclarations: PropertyDeclaration[],
        sheetDeclarations: PropertyDeclaration[],
    ): { namesChanged: boolean } {
        const prevDecls = new Map(this._propertyMap);
        const prevNames = new Set(this._propertyMap.keys());
        this.rebuildPropertyMap(inlineDeclarations, sheetDeclarations);

        // Clear stale runtime overrides
        for (const [name] of this._runtimeOverrides) {
            const prevDecl = prevDecls.get(name);
            const newDecl = this._propertyMap.get(name);
            if (!PropertyResolver._valuesEqual(prevDecl?.value, newDecl?.value)) {
                this._runtimeOverrides.delete(name);
                continue;
            }
            if (prevDecl?.inherits !== newDecl?.inherits) {
                this._runtimeOverrides.delete(name);
            }
        }

        // A commit keeps the states that show (CANOPY_STATES §8.4). Each stack
        // entry whose state the new data still defines stays — a pseudo entry
        // while some state still references it — and the rest are dropped.
        // Transitions and drivers stop first: their overlay belongs to the old
        // data. A non-empty stack then re-resolves instantly against the new
        // data; the override flags are left for that recompute to revert.
        //
        // An empty stack takes no recompute. External contributions (Keyframes)
        // stay in the map but dormant: the host's applyCommit has reverted what
        // they drove, and they re-take effect on their next push.
        const prevTopState = this.getActiveState();
        this._stateStack = this._stateStack.filter(e =>
            e.name.startsWith(':') ? this._hasPseudoReference(e.name) : this.hasState(e.name));
        // A pick keeps the part of it the new data still has (`X` of
        // `X:hover` once nothing references `:hover`); with none left it
        // hands back to the runtime slot.
        if (this._editorPick && !this._pickEntries(this._editorPick)) {
            const custom = this._editorPick.split(':')[0];
            const kept = custom && this.hasState(custom) ? custom : undefined;
            this._editorPick = kept;
            this._pickStack = kept ? [{ name: kept, priority: CUSTOM_STATE_PRIORITY }] : [];
        }
        this._invalidateEligibleStatesCache();
        this._baseValues.clear();
        this.stopAllTransitions();
        if (this._shown.length > 0) {
            this._recomputeFromStack(true, prevTopState);
        } else {
            this._hasActiveTransformOverride = false;
            this._hasActiveEnabledOverride = false;
            if (prevTopState !== undefined) this._callbacks.onStateChanged(prevTopState, undefined);
        }

        let namesChanged = prevNames.size !== this._propertyMap.size;
        if (!namesChanged) {
            for (const name of this._propertyMap.keys()) {
                if (!prevNames.has(name)) { namesChanged = true; break; }
            }
        }

        this._notifyAllSubscribers();

        for (const name of this._propertyMap.keys()) {
            this._cascadePropertyChange(name);
        }

        return { namesChanged };
    }

    // ── Descendant cascade ──

    private _cascadePropertyChange(name: string): void {
        for (const child of this._nodeAdapter.getChildren()) {
            this._cascadeToChild(child, name);
        }
    }

    private _cascadeToChild(child: CanopyNodeChild, name: string): void {
        const childProvider = child.getProvider();
        if (childProvider) {
            // Check if child has this property and if it inherits
            if (childProvider.hasProperty(name)) {
                const decl = (childProvider as PropertyResolver)._propertyMap?.get(name);
                if (decl?.inherits === false) return; // Pinned — cascade stops

                // Notify child's subscribers (its getValue() walks up to us)
                if (childProvider instanceof PropertyResolver) {
                    childProvider._notifySubscribers(name);
                    childProvider._cascadePropertyChange(name);
                }
                return;
            }
        }
        // No provider with this name — skip past and recurse
        for (const gc of child.getChildren()) {
            this._cascadeToChild(gc, name);
        }
    }

    // ── Subscription registry (CanopyProvider interface) ──

    subscribe(name: string, subscriber: CanopySubscriber): void {
        let set = this._subscribers.get(name);
        if (!set) {
            set = new Set();
            this._subscribers.set(name, set);
        }
        set.add(subscriber);
    }

    unsubscribe(name: string, subscriber: CanopySubscriber): void {
        const set = this._subscribers.get(name);
        if (set) {
            set.delete(subscriber);
            if (set.size === 0) this._subscribers.delete(name);
        }
    }

    unsubscribeAll(subscriber: CanopySubscriber): void {
        for (const [, set] of this._subscribers) {
            set.delete(subscriber);
        }
    }

    private _notifySubscribers(name: string): void {
        const set = this._subscribers.get(name);
        if (!set) return;
        for (const subscriber of set) {
            subscriber._onPropertyChanged(name);
        }
    }

    private _notifyAllSubscribers(): void {
        for (const [name, set] of this._subscribers) {
            for (const subscriber of set) {
                subscriber._onPropertyChanged(name);
            }
        }
    }

    // ── Cleanup ──

    /** Full cleanup on detach */
    dispose(): void {
        this.stopAllTransitions();
        // Resolve any exit barriers still pending (stopAllTransitions drains
        // active keys, but a barrier could hold keys that never started a
        // transition host entry) so awaiters don't hang after detach.
        const pending = this._exitBarriers;
        this._exitBarriers = [];
        for (const b of pending) b.resolve();
        this._propertyMap.clear();
        this._subscribers.clear();
        this._stateStack.length = 0;
        this._editorPick = undefined;
        this._pickStack = [];
        this._invalidateEligibleStatesCache();
        this._baseValues.clear();
        this._runtimeOverrides.clear();
        this._contributions.clear();
        this._hasActiveTransformOverride = false;
        this._hasActiveEnabledOverride = false;
    }

    /** Whether the state stack has active transform overrides */
    get hasActiveTransformOverride(): boolean {
        return this._hasActiveTransformOverride;
    }

    /** Get the base values map (for host transform revert) */
    getBaseValues(): ReadonlyMap<string, PropertyValue> {
        return this._baseValues;
    }

    /** Get the stack that shows (for host inspection) */
    getStateStack(): ReadonlyArray<ActiveStateEntry> {
        return this._shown;
    }

    // ── Static helpers ──

    /** Whether a property type supports interpolation */
    private static _isInterpolable(type: string): boolean {
        return type === 'number' || type === 'color' || type === 'vec3' || type === 'vec4' || type === 'Vector3';
    }

    /**
     * Whether a time transition has any observable timing — i.e. should start
     * a real transition entry rather than snap. Applies to ALL property types:
     * interpolable values ease; discrete (non-interpolable) values flip when
     * eased progress crosses 0.5 (CSS `allow-discrete` timing — see
     * `interpolateValue`) and the entry is live for `delay + duration`, so
     * discrete swaps are delayable, cancellable, and counted by exit barriers
     * (`setStateAwaited`) exactly like their interpolable siblings.
     * `{duration: 0, delay: X}` flips exactly at X — the stagger idiom
     * (CANOPY_STATES §6.2 rule 2).
     */
    private static _timeTransitionApplies(transition: TimeTransition): boolean {
        return transition.duration > 0 || (transition.delay ?? 0) > 0;
    }

    /** Structural equality for PropertyValue */
    static valuesEqual(a: PropertyValue | undefined, b: PropertyValue | undefined): boolean {
        return PropertyResolver._valuesEqual(a, b);
    }

    private static _valuesEqual(a: PropertyValue | undefined, b: PropertyValue | undefined): boolean {
        if (a === b) return true;
        if (a == null || b == null) return false;
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
                if (a[i] !== b[i]) return false;
            }
            return true;
        }
        return a === b;
    }

    /** Infer property type from a value */
    private static _inferType(value: PropertyValue): string {
        if (typeof value === 'number') return 'number';
        if (typeof value === 'boolean') return 'boolean';
        if (Array.isArray(value) && value.length === 4) return 'vec4';
        if (Array.isArray(value) && value.length === 3) return 'vec3';
        if (typeof value === 'string' && value.startsWith('#')) return 'color';
        return 'string';
    }

    /** Type-check a value against a property type */
    static typeCheck(type: string, value: PropertyValue): boolean {
        return PropertyResolver._typeCheck(type, value);
    }

    private static _typeCheck(type: string, value: PropertyValue): boolean {
        switch (type) {
            case 'string': return typeof value === 'string';
            case 'number': return typeof value === 'number';
            case 'boolean': return typeof value === 'boolean';
            case 'color': return typeof value === 'string';
            case 'vec3': return Array.isArray(value) && value.length === 3;
            case 'vec4': return Array.isArray(value) && value.length === 4;
            case 'Vector3': return Array.isArray(value) && value.length === 3;
            default: return false;
        }
    }
}
